import React from 'react';
import { computed, action, makeObservable } from 'mobx';
import { StaffAccountBasicDataType } from 'src/AppState/sdk/staff';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { Common } from 'src/AppState/Common';
import { convertCustomerDataForGamprotect } from './convertCustomerDataForGamprotect';
import { PermissionGrantsState } from 'src/AppState/PermissionGrants/PermissionGrants.state';
import { Permission } from 'src/AppState/PermissionGrants/PermissionGrants.types';
import { FilterOptionType } from 'src/components/RealityCheckForm/CustomDropdown';
import { GamprotectUserStore } from './Gamprotect.store';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { GamprotectReasonForm } from './GamprotectReasonForm';
import { ToasterState } from 'src/AppState/ToasterState';
import { ApiGamprotectRemoveResponseType } from 'src/api_openapi/v2/gamprotect/apiGamprotectRemove';
import { ApiGamprotectSubmitResponseType } from 'src/api_openapi/v2/gamprotect/apiGamprotectSubmit';
import { getErrorMessage } from './errorParser';

export const entryReasons: Record<string, string> = {
    'gambling-problem-or-addiction': 'Gambling problem or addiction',
    'seeking-external-support': 'Seeking external support',
    'risk-of-harming-themselves': 'Individuals may be at risk of harming themselves',
    'medical-condition': 'Medical condition',
    'want-to-stop-gambling-forever': 'Want to stop gambling forever',
};

const InputErrorMessage = 'This field is required.';

type PossibleActionType = {
    type: 'add' | 'remove';
    isAllowed: boolean;
    message?: string;
};

type SendRequestResultType =
    | {
          type: 'success';
      }
    | {
          type: 'error';
          error: string;
      };

export class GamprotectTabState {
    public gamprotectUserStore: GamprotectUserStore;
    public reason: FormInputState<string, string>;
    private popupStack: PopupsStackState;

    public constructor(
        private readonly customerId: number,
        private readonly models: ModelsState,
        private readonly permissionGrantsState: PermissionGrantsState,
        private readonly common: Common
    ) {
        makeObservable(this);

        this.popupStack = PopupsStackState.get(this.common);
        this.gamprotectUserStore = new GamprotectUserStore(this.common, customerId);
        this.reason = FormInputState.new('').map(validateNotEmpty(InputErrorMessage));
    }

    @computed private get hasManagementPermission(): boolean {
        if (this.permissionGrantsState.has(Permission.accountsGamprotectMgmt)) {
            return true;
        }

        return false;
    }

    @computed private get basicDataUserModel(): StaffAccountBasicDataType | null {
        return this.models.getUserModel(this.customerId).basicData.data;
    }

    @computed public get possibleAction(): PossibleActionType | null {
        const hasGamprotect = this.gamprotectUserStore.hasGamprotect;
        const hasGamprotectOnMyBrand = this.gamprotectUserStore.hasGamprotectOnMyBrand;

        if (hasGamprotect === null || hasGamprotectOnMyBrand === null) {
            return null;
        }

        if (hasGamprotectOnMyBrand) {
            return {
                type: 'remove',
                isAllowed: this.hasManagementPermission,
                message: this.hasManagementPermission === false ? "You don't have the required permissions" : undefined,
            };
        }

        if (hasGamprotect) {
            return {
                type: 'remove',
                isAllowed: false,
                message: "You can't remove a marker added by another brand",
            };
        }

        return {
            type: 'add',
            isAllowed: this.hasManagementPermission,
            message: this.hasManagementPermission === false ? "You don't have the required permissions" : undefined,
        };
    }

    @action public handleButtonClick = (): void => {
        const possibleAction = this.possibleAction;

        if (possibleAction === null) {
            return;
        }

        this.reason.reset();

        const title = possibleAction.type === 'remove' ? 'Remove Gamprotect Marker' : 'Submit Gamprotect Marker';

        this.popupStack.addPopup('gamprotect_action', {
            template: 'generic',
            hasOverlay: true,
            title,
            message: <GamprotectReasonForm state={this} />,
            confirm_label: possibleAction.type === 'add' ? 'Submit to Gamprotect' : undefined,
            delete_label: possibleAction.type === 'remove' ? 'Remove from Gamprotect' : undefined,
            cancel_label: 'Cancel',
            isLoading: false,
            onConfirm: async () => await this.handleConfirmationButton(),
            onDelete: async () => await this.handleConfirmationButton(),
            onCancel: () => this.popupStack.closePopupOnTop(),
        });

        return;
    };

    @action private setPopupLoading = (isLoading: boolean): void => {
        const popup = this.popupStack.topPopup;

        if (popup !== undefined) {
            popup.isLoading = isLoading;
        }
    };

    @action private handleConfirmationButton = async (): Promise<void> => {
        this.setPopupLoading(true);
        const result = await this.sendRequest();

        this.setPopupLoading(false);

        if (result.type === 'success') {
            ToasterState.get(this.common).setToast({
                variant: 'success',
                title: 'Success',
                message: 'The request was sent successfully.',
            });

            this.popupStack.closePopupOnTop();
            return;
        }

        ToasterState.get(this.common).setToast({
            variant: 'error',
            title: 'Error!',
            message: result.error,
        });

        return;
    };

    @action private sendRequest = async (): Promise<SendRequestResultType> => {
        this.reason.setAsVisited();

        if (this.reason.result.value.type === 'error') {
            return {
                type: 'error',
                error: this.reason.errorsForView[0] ?? '',
            };
        }

        if (this.possibleAction === null) {
            return {
                type: 'error',
                error: `Unknown error occured. Please try again.`,
            };
        }

        const gamprotectParsedData = convertCustomerDataForGamprotect(this.basicDataUserModel);

        if (gamprotectParsedData.success === false) {
            return {
                type: 'error',
                error: `User data error: ${gamprotectParsedData.message}`,
            };
        }

        try {
            let result: ApiGamprotectSubmitResponseType | ApiGamprotectRemoveResponseType;

            switch (this.possibleAction.type) {
                case 'add': {
                    result = await this.gamprotectUserStore.handleSubmit(
                        gamprotectParsedData,
                        this.reason.result.value.data
                    );

                    break;
                }
                case 'remove': {
                    result = await this.gamprotectUserStore.handleRemove(
                        gamprotectParsedData,
                        this.reason.result.value.data
                    );
                    break;
                }
            }
            await this.gamprotectUserStore.refreshData();
            if (result.status === 200) {
                return {
                    type: 'success',
                };
            }
            if (result.status === 500) {
                return {
                    type: 'error',
                    error: getErrorMessage(result.body.error.message),
                };
            }
            return {
                type: 'error',
                error: `Cannot send request - Recived status: ${result.status}`,
            };
        } catch (e) {
            return {
                type: 'error',
                error: `Unknown error occured. Please try again.`,
            };
        }
    };

    public entryReasons = (): Array<FilterOptionType<string>> => {
        const out: Array<FilterOptionType<string>> = [];

        for (const [key, value] of Object.entries(entryReasons)) {
            out.push({
                value: key,
                label: value,
            });
        }

        return out;
    };

    @action public setReason = (reason: string): void => {
        this.reason.setValue(reason);
    };
}
